/**
 * WebSignPanel - Main class
 * Manages the signature panel interface
 */

import { SignerList } from './components/SignerList.js';
import { PDFViewer } from './components/PDFViewer.js';
import { SignerImage } from './components/SignerImage.js';
import { ActionBar } from './components/ActionBar.js';
import { ConfirmDialog } from './components/ConfirmDialog.js';
import { PdfModal } from './components/PdfModal.js';
import { SignatureState } from './core/SignatureState.js';
import { DataParser } from './core/DataParser.js';
import { WebSignService } from './services/WebSignService.js';
import { i18n } from './utils/i18n.js';

// Fixed container ID
const CONTAINER_ID = 'web-sign-panel';

export class WebSignPanel {
  /**
   * @param {File|Blob|string} pdfData - PDF file, blob, base64 string, or URL
   * @param {Object} config - Configuration JSON object
   * @param {Function} callback - Callback function for events (completed, cancel, error)
   */
  constructor(pdfData, config, callback = () => {}) {
    // Store callback
    this.callback = callback;

    // Validate required parameters
    if (!config) {
      this.callback({ type: 'error', error: 'WebSignPanel: config parameter is required' });
      return;
    }

    // Validate PDF data is provided
    if (!pdfData) {
      this.callback({ type: 'error', error: 'WebSignPanel: pdfData parameter is required' });
      return;
    }

    // Store original config JSON for later updates
    this.originalConfig = config;

    // Parse configuration from config object
    const parsedConfig = DataParser.parseConfiguration(config);

    // Set language from settings
    const language = parsedConfig.settings?.language || 'es';
    i18n.setLanguage(language);

    // Custom texts from settings.labels (reset on every new panel)
    i18n.setOverrides(parsedConfig.settings?.labels);

    // Build final configuration
    // PDF data only comes from constructor parameter
    this.config = {
      signers: parsedConfig.signers || [],
      signerLists: parsedConfig.signerLists || [],
      pdfData: pdfData || null,
      integrationData: parsedConfig.integrationData || {},
      settings: parsedConfig.settings || {}
    };

    // Get container element (fixed ID: web-sign-panel)
    this.container = document.getElementById(CONTAINER_ID);

    if (!this.container) {
      this.callback({ type: 'error', error: `WebSignPanel: container element with id="${CONTAINER_ID}" not found` });
      return;
    }

    // Initialize state
    this.state = new SignatureState(this.config.signers, this.config.integrationData, this.config.settings);

    // Services
    this.webSignService = new WebSignService();

    // State
    this.currentSignedPDF = null; // Track signed PDF for multiple signatures

    // Components
    this.signerList = null;
    this.pdfViewer = null;
    this.signerImage = null;
    this.actionBar = null;

    // State
    this.selectedSigner = null;

    // Initialize
    this.init();
  }

  /**
   * Initialize the panel
   */
  init() {
    // Create main structure
    this.createStructure();

    // Initialize components
    this.signerList = new SignerList({
      container: this.container.querySelector('.wsp-signer-list'),
      state: this.state,
      onSignerSignClick: (signer) => this.handleSignerSignClick(signer),
      onSignerSelect: (signer) => this.handleSignerSelect(signer),
      isLocked: this.config.settings.isLocked || false,
      signerLists: this.config.signerLists || []
    });

    this.pdfViewer = new PDFViewer({
      container: this.container.querySelector('.wsp-pdf-viewer'),
      pdfData: this.config.pdfData
    });

    this.signerImage = new SignerImage({
      container: this.container.querySelector('.wsp-signer-image'),
      signer: null
    });

    this.actionBar = new ActionBar({
      container: this.container.querySelector('.wsp-action-bar'),
      onCancel: () => this.handleCancel(),
      onViewPdf: () => this.handleViewPdf(),
      onPrint: () => this.handlePrint(),
      onAccept: () => this.handleAccept(),
      onFinishAndPrint: () => this.handleFinishAndPrint(),
      settings: this.config.settings
    });

    // Listen to state changes
    this.state.on('change', () => this.handleStateChange());

    // Initial render
    this.render();
    this.pdfViewer.render();

    // Check for autoSign after initial render
    this.checkAutoSign();
  }

  /**
   * Check if there's a signer with autoSign enabled and trigger it
   */
  async checkAutoSign() {
    // Don't autoSign if document is locked
    if (this.config.settings.isLocked) {
      return;
    }

    // Find the first unsigned signer with autoSign enabled
    const autoSignSigner = this.state.getSigners().find(
      signer => signer.autoSign && !signer.isSigned
    );

    if (autoSignSigner) {
      // Wait a bit for the UI to render
      setTimeout(() => {
        this.handleSignerSignClick(autoSignSigner);
      }, 500);
    }
  }

  /**
   * Create HTML structure
   */
  createStructure() {
    this.container.innerHTML = `
      <div class="w-full h-full flex flex-col bg-gray-50 relative">
        <!-- WebSign Modal (relative to panel container) -->
        <div id="wsp-websign-modal" class="absolute inset-0 z-50 hidden" aria-labelledby="modal-title" role="dialog" aria-modal="true">
          <!-- Background overlay -->
          <div class="absolute inset-0 bg-gray-500 bg-opacity-75 transition-opacity"></div>
          
          <!-- Modal panel -->
          <div class="absolute inset-0 z-10 overflow-y-auto">
            <div class="flex min-h-full items-center justify-center p-4 sm:p-4 p-0">
              <!-- Modal content wrapper -->
              <div id="wsp-websign-wrapper" class="relative transform overflow-hidden rounded-lg bg-white shadow-xl transition-all sm:rounded-lg rounded-none w-full sm:w-auto max-w-full">
                <!-- WebSign container (required by WebSign library) -->
                <div id="web-sign" class="h-[228px]"></div>
              </div>
            </div>
          </div>
        </div>

        <div class="flex-1 flex gap-4 p-4 overflow-hidden relative">
          <!-- Overlay for mobile (left sidebar) -->
          <div id="wsp-overlay" class="lg:hidden absolute inset-0 bg-black bg-opacity-50 z-30 hidden"></div>

          <!-- Overlay for mobile (right sidebar - image) -->
          <div id="wsp-image-overlay" class="lg:hidden absolute inset-0 bg-black bg-opacity-50 z-30 hidden"></div>

          <!-- Left Sidebar (Signers) -->
          <div id="wsp-sidebar" class="absolute lg:static inset-y-0 left-0 z-40 w-80 transform -translate-x-full lg:translate-x-0 transition-transform duration-300 ease-in-out lg:flex-shrink-0 wsp-signer-list"></div>
          
          <!-- Main content (PDF Viewer) -->
          <div class="flex-1 min-w-0 relative">
            <!-- Mobile menu button - left (signers) -->
            <button id="wsp-mobile-menu-btn" class="lg:hidden absolute top-2 left-2 z-10 p-2 bg-white rounded-lg shadow-lg border border-gray-200 hover:bg-gray-50 transition-colors">
              <svg class="w-6 h-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16"/>
              </svg>
            </button>

            <!-- Mobile menu button - right (image) - hidden by default -->
            <button id="wsp-mobile-image-btn" class="lg:hidden absolute top-2 right-2 z-10 p-2 bg-white rounded-lg shadow-lg border border-gray-200 hover:bg-gray-50 transition-colors hidden">
              <svg class="w-6 h-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
              </svg>
            </button>
            
            <!-- PDF Viewer -->
            <div class="wsp-pdf-viewer h-full"></div>
          </div>

          <!-- Right Sidebar (Signer Image) - Hidden by default, slides from right on mobile -->
          <div id="wsp-image-sidebar" class="absolute lg:static inset-y-0 right-0 z-40 w-64 transform translate-x-full lg:translate-x-0 transition-transform duration-300 ease-in-out lg:flex-shrink-0 hidden wsp-signer-image"></div>
        </div>
        
        <div class="border-t border-gray-200 bg-white wsp-action-bar"></div>
      </div>
    `;

    // Setup mobile menu toggle
    this.setupMobileMenu();
    this.setupImageSidebar();
  }

  /**
   * Setup mobile menu functionality
   */
  setupMobileMenu() {
    const menuBtn = this.container.querySelector('#wsp-mobile-menu-btn');
    const sidebar = this.container.querySelector('#wsp-sidebar');
    const overlay = this.container.querySelector('#wsp-overlay');

    const toggleMenu = () => {
      const isOpen = !sidebar.classList.contains('-translate-x-full');
      
      if (isOpen) {
        // Close
        sidebar.classList.add('-translate-x-full');
        overlay.classList.add('hidden');
      } else {
        // Open
        sidebar.classList.remove('-translate-x-full');
        overlay.classList.remove('hidden');
      }
    };

    if (menuBtn) {
      menuBtn.addEventListener('click', toggleMenu);
    }

    if (overlay) {
      overlay.addEventListener('click', toggleMenu);
    }
  }

  /**
   * Setup image sidebar functionality for mobile
   */
  setupImageSidebar() {
    const imageBtn = this.container.querySelector('#wsp-mobile-image-btn');
    const imageSidebar = this.container.querySelector('#wsp-image-sidebar');
    const imageOverlay = this.container.querySelector('#wsp-image-overlay');

    const toggleImageSidebar = () => {
      const isOpen = !imageSidebar.classList.contains('translate-x-full');
      
      if (isOpen) {
        // Close
        imageSidebar.classList.add('translate-x-full');
        imageOverlay.classList.add('hidden');
      } else {
        // Open
        imageSidebar.classList.remove('translate-x-full');
        imageOverlay.classList.remove('hidden');
      }
    };

    if (imageBtn) {
      imageBtn.addEventListener('click', toggleImageSidebar);
    }

    if (imageOverlay) {
      imageOverlay.addEventListener('click', toggleImageSidebar);
    }
  }

  /**
   * Handle signer selection
   */
  handleSignerSelect(signer) {
    this.selectedSigner = signer;
    
    const imageSidebar = this.container.querySelector('#wsp-image-sidebar');
    const imageBtn = this.container.querySelector('#wsp-mobile-image-btn');
    
    // Show/hide image sidebar based on whether signer has image
    if (signer && (signer.img1B64 || signer.img2B64)) {
      // Show sidebar (visible on desktop, hidden on mobile until button clicked)
      if (imageSidebar) {
        imageSidebar.classList.remove('hidden');
      }
      
      // Show mobile button
      if (imageBtn) {
        imageBtn.classList.remove('hidden');
      }
      
      // Update image component
      this.signerImage.signer = signer;
      this.signerImage.render();
    } else {
      // Hide sidebar
      if (imageSidebar) {
        imageSidebar.classList.add('hidden');
        imageSidebar.classList.add('translate-x-full');
      }
      
      // Hide mobile button
      if (imageBtn) {
        imageBtn.classList.add('hidden');
      }
      
      // Hide overlay
      const imageOverlay = this.container.querySelector('#wsp-image-overlay');
      if (imageOverlay) {
        imageOverlay.classList.add('hidden');
      }
      
      // Clear image component
      this.signerImage.signer = null;
      this.signerImage.render();
    }
  }

  /**
   * Show WebSign modal
   */
  showWebSignModal() {
    const modal = this.container.querySelector('#wsp-websign-modal');
    if (modal) {
      modal.classList.remove('hidden');
    }
  }

  /**
   * Hide WebSign modal
   */
  hideWebSignModal() {
    const modal = this.container.querySelector('#wsp-websign-modal');
    if (modal) {
      modal.classList.add('hidden');
      // Clear WebSign content
      const webSignDiv = document.getElementById('web-sign');
      if (webSignDiv) {
        webSignDiv.innerHTML = '';
      }
    }
  }

  /**
   * Show bridge required dialog with open button
   */
  showBridgeDialog() {
    const dialogId = 'wsp-bridge-dialog';
    // Remove existing dialog if any
    const existing = this.container.querySelector(`#${dialogId}`);
    if (existing) existing.remove();

    const dialog = document.createElement('div');
    dialog.id = dialogId;
    dialog.className = 'fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50';
    dialog.innerHTML = `
      <div class="bg-white rounded-lg shadow-xl p-6 mx-4 max-w-sm w-full">
        <div class="flex items-center gap-3 mb-4">
          <svg class="w-8 h-8 text-yellow-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/>
          </svg>
          <p class="text-gray-800 text-sm">${i18n.t('error.bridgeRequired')}</p>
        </div>
        <div class="flex gap-3">
          <button id="wsp-bridge-close" class="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors">
            ${i18n.t('button.cancel')}
          </button>
          <button id="wsp-bridge-open" class="flex-1 px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 transition-colors">
            ${i18n.t('bridge.open')}
          </button>
        </div>
      </div>
    `;

    this.container.appendChild(dialog);

    dialog.querySelector('#wsp-bridge-open').addEventListener('click', () => {
      window.location.href = 'websignbridge://';
      dialog.remove();
    });

    dialog.querySelector('#wsp-bridge-close').addEventListener('click', () => {
      dialog.remove();
    });

    // Close on backdrop click
    dialog.addEventListener('click', (e) => {
      if (e.target === dialog) dialog.remove();
    });
  }

  /**
   * Render all components
   */
  render() {
    this.signerList.render();
    this.actionBar.render({
      canAccept: this.canAccept(),
      // Cancel stays enabled until a mandatory signature has been collected
      canCancel: !this.state.hasMandatorySignature(),
      acceptLabelKey: this.getAcceptLabelKey(),
      // Accept + Print keeps requiring all mandatory signatures
      canFinishAndPrint: this.state.canComplete()
    });
  }

  /**
   * Accept button ("Diferir firma" / "Terminar") is enabled as soon as any
   * signer has signed, or when there is nobody left to sign.
   */
  canAccept() {
    return this.state.hasAnySignature() || this.state.isComplete();
  }

  /**
   * Accept button label depending on signature progress:
   * - All signers signed       -> "Terminar"
   * - Any signature pending    -> "Diferir firma"
   */
  getAcceptLabelKey() {
    return this.state.isComplete() ? 'actionBar.finish' : 'actionBar.accept';
  }

  /**
   * Handle signer sign button click
   */
  async handleSignerSignClick(signer) {
    if (signer.isSigned) {
      return;
    }

    try {
      const signatureData = await this.captureSignature(signer);
      this.state.markAsSigned(signer._index, signatureData);
    } catch (error) {
      // Don't show alert if user cancelled
      if (error.message === 'CANCELLED') {
        return;
      }
      
      // Show dialog for bridge required error
      if (error.message === i18n.t('error.bridgeRequired')) {
        this.showBridgeDialog();
        return;
      }
      
      console.error('Signature capture failed:', error);
      this.callback({ type: 'error', error: error.message });
    }
  }

  /**
   * Capture signature using WebSign
   */
  async captureSignature(signer) {
    try {
      this.showWebSignModal();
      const pdfFile = await this.getPDFFile();
      if (!pdfFile) {
        this.hideWebSignModal();
        throw new Error(i18n.t('error.noPdf'));
      }
      const result = await this.webSignService.captureSignature(
        signer,
        pdfFile,
        this.config
      );
      this.hideWebSignModal();

      if (result.success) {
        if (result.signedFile) {
          this.currentSignedPDF = result.signedFile;
          await this.updateSignedPDF(result.signedFile);
        }
        return {
          timestamp: result.timestamp,
          signerId: signer.signerId
        };
      } else if (result.cancelled) {
        // User cancelled - throw error to stop the signing process
        throw new Error('CANCELLED');
      } else {
        throw new Error(result.error || 'Signature capture failed');
      }
    } catch (error) {
      this.hideWebSignModal();
      throw error;
    }
  }

  /**
   * Get PDF file for signing (current signed version or original)
   */
  async getPDFFile() {
    // If we have a signed PDF from previous signature, use that
    if (this.currentSignedPDF) {
      return this.currentSignedPDF;
    }

    // Otherwise, get the original PDF
    const pdfData = this.config.pdfData;
    
    if (!pdfData) {
      return null;
    }

    // If it's already a File or Blob, return it
    if (pdfData instanceof File || pdfData instanceof Blob) {
      return pdfData;
    }

    // If it's a string, determine what type
    if (typeof pdfData === 'string') {
      try {
        // Check if it's a data URL (data:application/pdf;base64,...)
        if (pdfData.startsWith('data:')) {
          const base64 = pdfData.split(',')[1];
          const binaryString = atob(base64);
          const bytes = new Uint8Array(binaryString.length);
          for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
          }
          const blob = new Blob([bytes], { type: 'application/pdf' });
          return new File([blob], 'document.pdf', { type: 'application/pdf' });
        }
        
        // Check if it's a URL or file path
        const isUrl = pdfData.startsWith('http://') || 
                      pdfData.startsWith('https://') || 
                      pdfData.startsWith('/') ||
                      pdfData.startsWith('./') ||
                      pdfData.startsWith('../') ||
                      pdfData.endsWith('.pdf');
        
        if (isUrl) {
          // Fetch from the exact URL provided
          const response = await fetch(pdfData);
          
          if (!response.ok) {
            throw new Error(`Failed to fetch PDF: ${response.status} ${response.statusText}`);
          }
          
          const blob = await response.blob();
          return new File([blob], 'document.pdf', { type: 'application/pdf' });
        }
        
        // Otherwise, assume it's raw base64 (without data: prefix)
        const binaryString = atob(pdfData);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        const blob = new Blob([bytes], { type: 'application/pdf' });
        return new File([blob], 'document.pdf', { type: 'application/pdf' });
        
      } catch (error) {
        console.error('Failed to convert PDF data:', error);
        return null;
      }
    }

    return null;
  }

  /**
   * Handle state change
   */
  handleStateChange() {
    this.render();
    
    // Update image sidebar if a signer is selected
    if (this.selectedSigner) {
      const updatedSigner = this.state.getSigner(this.selectedSigner._index);
      if (updatedSigner) {
        this.selectedSigner = updatedSigner;
        this.signerImage.signer = updatedSigner;
        this.signerImage.render();
      }
    }
  }

  /**
   * Handle cancel button
   */
  handleCancel() {
    if (this.state.hasMandatorySignature()) {
      return;
    }

    if (confirm(i18n.t('confirm.cancel'))) {
      this.callback({ type: 'cancel' });
    }
  }

  /**
   * Handle View PDF button: shows the current document (signed version if
   * any) in a modal with download and print actions.
   * Downloading emits the `download` event; printing from the modal prints
   * directly (no `print` event, doNotPrint / showWarningBeforePrint not applied).
   */
  handleViewPdf() {
    const pdfData = this.currentSignedPDF || this.config.pdfData;
    if (!pdfData) {
      this.callback({ type: 'error', error: i18n.t('error.noPdf') });
      return;
    }

    new PdfModal({
      container: this.container,
      pdfData,
      onDownload: (blob) => {
        this.callback({
          type: 'download',
          data: blob,
          config: this.getUpdatedConfig()
        });
      },
      onError: (error) => this.callback({ type: 'error', error })
    }).render();
  }

  /**
   * Handle print button
   */
  async handlePrint() {
    const { showWarningBeforePrint, doNotPrint } = this.config.settings;

    if (showWarningBeforePrint) {
      const confirmed = await this.showPrintConfirmDialog();
      if (!confirmed) {
        return;
      }
    }

    const willPrint = !doNotPrint;

    this.callback({
      type: 'print',
      data: this.currentSignedPDF,
      config: this.getUpdatedConfig(),
      printed: willPrint
    });

    if (willPrint) {
      await this.printDocument();
    }
  }

  /**
   * Show print confirmation dialog.
   * @returns {Promise<boolean>} true if the user confirms, false otherwise
   */
  showPrintConfirmDialog() {
    return new Promise((resolve) => {
      new ConfirmDialog({
        container: this.container,
        title: i18n.t('confirm.printTitle'),
        message: i18n.t('confirm.print'),
        confirmLabel: i18n.t('button.print'),
        cancelLabel: i18n.t('button.cancel'),
        onConfirm: () => resolve(true),
        onCancel: () => resolve(false)
      }).render();
    });
  }

  /**
   * Open the browser print dialog for the current PDF.
   * Does not emit events nor check print-related settings.
   */
  async printDocument() {
    try {
      // Try to print the PDF from the viewer
      if (this.pdfViewer) {
        await this.pdfViewer.print();
      } else {
        // Fallback to window.print()
        window.print();
      }
    } catch (error) {
      console.error('Failed to print PDF:', error);
      this.callback({ type: 'error', error: i18n.t('error.print') + ': ' + error.message });
    }
  }

  /**
   * Handle accept button
   */
  handleAccept() {
    // "Diferir firma" / "Terminar": requires at least one signature
    if (!this.canAccept()) {
      return;
    }

    // Same event for both labels. allSigned / mandatoryCompleted tell the
    // integrator whether the process was finished or deferred.
    this.callback({
      type: 'completed',
      data: this.currentSignedPDF,
      config: this.getUpdatedConfig(),
      allSigned: this.state.isComplete(),
      mandatoryCompleted: this.state.canComplete()
    });
  }

  /**
   * Handle finish and print button - validates mandatory, then completes
   * with a dedicated state so the integrator can print after saving.
   * Printing is NOT triggered here: the integrator must call printPdf()
   * once the document has been saved successfully.
   */
  async handleFinishAndPrint() {
    if (!this.state.canComplete()) {
      alert(i18n.t('confirm.mandatoryRequired'));
      return;
    }

    this.callback({
      type: 'completedPrintingPending',
      data: this.currentSignedPDF,
      config: this.getUpdatedConfig()
    });
  }

  /**
   * Get updated config JSON with current signature states
   */
  getUpdatedConfig() {
    // Clone the original config
    const updatedConfig = JSON.parse(JSON.stringify(this.originalConfig));

    // Remove sensitive data
    if (updatedConfig.settings) {
      delete updatedConfig.settings.bearerToken;
    }

    // Update signers with current state
    const currentSigners = this.state.getSigners();
    updatedConfig.signers = updatedConfig.signers.map((signer, index) => {
      const currentSigner = currentSigners.find(s => s._index === index);
      if (currentSigner) {
        return {
          ...signer,
          isSigned: currentSigner.isSigned,
          timeStamp: currentSigner.timestamp || ''
        };
      }
      return signer;
    });

    return updatedConfig;
  }

  /**
   * Update signers
   */
  updateSigners(signers) {
    this.state.updateSigners(signers);
    this.render();
  }

  /**
   * Load PDF
   */
  async loadPDF(pdfData) {
    this.config.pdfData = pdfData;
    await this.pdfViewer.loadPDF(pdfData);
  }

  /**
   * Update PDF with signed version
   */
  async updateSignedPDF(signedPDF) {
    try {
      this.currentSignedPDF = signedPDF;
      await this.pdfViewer.loadPDF(signedPDF);
    } catch (error) {
      console.error('Failed to update PDF viewer with signed PDF:', error);
    }
  }

  /**
   * Get current state
   */
  getState() {
    return {
      signers: this.state.getSigners(),
      signatures: this.state.getAllSignatures(),
      integrationData: this.state.getIntegrationData(),
      isComplete: this.state.isComplete(),
      canComplete: this.state.canComplete(),
      hasMandatorySignature: this.state.hasMandatorySignature()
    };
  }

  /**
   * Print the signed PDF document.
   *
   * Public API: intended to be called by the integrator after the document
   * has been saved successfully (typically after receiving the
   * `completedPrintingPending` callback). Opens the browser print dialog for
   * the document.
   *
   * Note: it is not possible to reliably detect when the print dialog is
   * closed (printed or cancelled) across browsers, so closing the panel or the
   * window must be decided by the integrator independently of printing.
   */
  async printPdf() {
    // Direct print: does not emit the `print` event nor apply
    // showWarningBeforePrint / doNotPrint (those only apply to the Print button)
    await this.printDocument();
  }

  /**
   * Destroy panel
   */
  destroy() {
    this.state.removeAllListeners();
    this.container.innerHTML = '';
  }
}
